import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { PageTitleDraft } from './pageTitleDraft';

// Scoped by org/project/item; retained only until a write succeeds after departure.
const drafts = new Map<string, PageTitleDraft>();
const mounts = new WeakMap<PageTitleDraft, number>();
let unloadInstalled = false;

export function usePageTitleDraft(key: string, storedTitle: string, save: (title: string) => Promise<void>) {
  const draft = useMemo(() => {
    let value = drafts.get(key);
    if (!value) { value = new PageTitleDraft(storedTitle, save); drafts.set(key, value); }
    return value;
  }, [key]);
  draft.setWriter(save);
  const state = useSyncExternalStore(draft.subscribe, draft.snapshot, draft.snapshot);
  useEffect(() => draft.remote(storedTitle), [draft, storedTitle]);
  useEffect(() => {
    mounts.set(draft, (mounts.get(draft) ?? 0) + 1);
    if (!unloadInstalled) {
      unloadInstalled = true;
      window.addEventListener('beforeunload', event => {
        if ([...drafts.values()].some(value => value.snapshot().status !== 'saved')) {
          event.preventDefault(); event.returnValue = '';
        }
      });
    }
    return () => {
      mounts.set(draft, Math.max(0, (mounts.get(draft) ?? 1) - 1));
      void draft.flush().then(() => {
        if (!mounts.get(draft) && draft.snapshot().status === 'saved' && drafts.get(key) === draft) drafts.delete(key);
      });
    };
  }, [draft, key]);
  return { ...state, rename: (title: string) => draft.rename(title), retry: () => { void draft.flush(); } };
}
